import type { VolumeVocabularyEntry } from "./types";

export type VolumeVocabularySort = "frequency" | "alphabetical" | "pages";
export const VOLUME_VOCABULARY_PAGE_SIZE = 25;

export interface VolumeVocabularyPage {
  items: VolumeVocabularyEntry[];
  page: number;
  totalPages: number;
  start: number;
  end: number;
  total: number;
}

const japanesePartOfSpeech = new Map([
  ["動詞", "Verb"],
  ["名詞", "Noun"],
  ["形容詞", "い-adjective"],
  ["形状詞", "な-adjective"],
  ["副詞", "Adverb"],
  ["連体詞", "Prenominal"],
  ["代名詞", "Pronoun"],
  ["感動詞", "Interjection"],
  ["接頭辞", "Prefix"],
  ["接尾辞", "Suffix"]
]);

const japaneseCollator = new Intl.Collator("ja", { numeric: true, sensitivity: "base" });

function searchableText(word: VolumeVocabularyEntry): string {
  return [
    word.expression,
    word.lemma,
    word.reading ?? "",
    ...word.definitions,
    ...word.labels,
    ...word.partOfSpeech
  ].join("\n").toLocaleLowerCase("ja");
}

export function filterAndSortVolumeVocabulary(
  words: VolumeVocabularyEntry[],
  query: string,
  sort: VolumeVocabularySort
): VolumeVocabularyEntry[] {
  const normalizedQuery = query.trim().toLocaleLowerCase("ja");
  const filtered = normalizedQuery
    ? words.filter((word) => searchableText(word).includes(normalizedQuery))
    : words;

  return [...filtered].sort((left, right) => {
    if (sort === "alphabetical") {
      return japaneseCollator.compare(left.expression, right.expression) || right.count - left.count;
    }
    if (sort === "pages") {
      return right.pageCount - left.pageCount || right.count - left.count || japaneseCollator.compare(left.expression, right.expression);
    }
    return right.count - left.count || right.pageCount - left.pageCount || japaneseCollator.compare(left.expression, right.expression);
  });
}

export function volumeVocabularyIdentity(word: Pick<VolumeVocabularyEntry, "expression" | "lemma">): string {
  return (word.lemma || word.expression).trim().normalize("NFKC").toLocaleLowerCase("ja");
}

export function paginateVolumeVocabulary(
  words: VolumeVocabularyEntry[],
  requestedPage: number,
  pageSize = VOLUME_VOCABULARY_PAGE_SIZE
): VolumeVocabularyPage {
  const safePageSize = Math.max(1, Math.floor(pageSize));
  const totalPages = Math.max(1, Math.ceil(words.length / safePageSize));
  const page = Math.min(totalPages, Math.max(1, Math.floor(requestedPage) || 1));
  const offset = (page - 1) * safePageSize;
  const items = words.slice(offset, offset + safePageSize);
  return {
    items,
    page,
    totalPages,
    start: items.length > 0 ? offset + 1 : 0,
    end: offset + items.length,
    total: words.length
  };
}

export function topVolumeVocabulary(words: VolumeVocabularyEntry[], limit = 15): VolumeVocabularyEntry[] {
  if (limit <= 0) return [];
  return [...words]
    .sort((left, right) => right.count - left.count || japaneseCollator.compare(left.expression, right.expression))
    .slice(0, limit);
}

export function volumeVocabularyProgress(processedPages: number, totalPages: number): number {
  if (totalPages <= 0) return 0;
  return Math.min(1, Math.max(0, processedPages / totalPages));
}

export function formatVolumeVocabularyPartOfSpeech(word: VolumeVocabularyEntry): string[] {
  const labels = [...word.labels];
  for (const part of word.partOfSpeech) {
    const translated = japanesePartOfSpeech.get(part);
    if (translated) labels.unshift(translated);
  }
  return [...new Set(labels)].slice(0, 3);
}
